import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useCurrentBrand } from '@/lib/branding/useCurrentBrand';

export type AssistantContextKind =
  | 'home'
  | 'brand'
  | 'project'
  | 'event'
  | 'session';

export interface AssistantContextRefs {
  brandId?: number;
  projectId?: number;
  eventId?: number;
  sessionId?: number;
}

export interface AssistantContext {
  kind: AssistantContextKind;
  refs: AssistantContextRefs;
  fingerprint: string;
}

/**
 * Derives the assistant's context from the current route.
 *
 * Match order matters: the nested event route (/projects/:id/events/:eventId)
 * MUST be tested before the bare project route. Routes are the source of truth
 * — `useCurrentBrand()` is only consulted for the dashboard fallback so the
 * Home prompt has at least one brand to anchor against.
 */
export function useAIContext(): AssistantContext {
  const loc = useLocation();
  const params = useParams();
  const { brand } = useCurrentBrand();

  return useMemo<AssistantContext>(() => {
    const path = loc.pathname;
    const idNum = (v: string | undefined) =>
      v != null && /^\d+$/.test(v) ? Number(v) : undefined;

    if (/^\/projects\/\d+\/events\/\d+/.test(path)) {
      const projectId = idNum(params.id);
      const eventId = idNum(params.eventId);
      return {
        kind: 'event',
        refs: { projectId, eventId },
        fingerprint: `event:${eventId ?? '?'}`,
      };
    }
    if (path.startsWith('/projects/')) {
      const projectId = idNum(params.id);
      return {
        kind: 'project',
        refs: { projectId },
        fingerprint: `project:${projectId ?? '?'}`,
      };
    }
    if (path.startsWith('/brand/')) {
      const brandId = idNum(params.id);
      return {
        kind: 'brand',
        refs: { brandId },
        fingerprint: `brand:${brandId ?? '?'}`,
      };
    }
    if (path.startsWith('/stations/')) {
      const sessionId = idNum(params.id);
      return {
        kind: 'session',
        refs: { sessionId },
        fingerprint: `session:${sessionId ?? '?'}`,
      };
    }
    // Home — fall back to the user's current brand so the prompt has anchor.
    return {
      kind: 'home',
      refs: { brandId: brand?.id },
      fingerprint: `home:${brand?.id ?? 'global'}`,
    };
  }, [loc.pathname, params.id, params.eventId, brand?.id]);
}
